"use client"

import { useRef, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Loader2 } from "lucide-react"

import {
  AUTH_CARD,
  AUTH_LINK,
  AUTH_SECONDARY,
  AUTH_SUBMIT,
  AUTH_TITLE,
} from "@/components/auth/auth-styles"
import { GoogleIcon } from "@/components/auth/google-icon"
import { PasswordInput } from "@/components/password-input"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authClient } from "@/lib/auth-client"
import { authErrorMessage } from "@/lib/auth-errors"

type FieldErrors = { email?: string; password?: string }

export function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Google login errors come back as ?error=... via errorCallbackURL
  const [formError, setFormError] = useState(
    searchParams.get("error")
      ? "Anmeldung mit Google fehlgeschlagen. Ist deine Adresse freigeschaltet?"
      : ""
  )
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [loading, setLoading] = useState(false)

  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const clearError = (field: keyof FieldErrors) => {
    setFormError("")
    setFieldErrors((errors) => ({ ...errors, [field]: undefined }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // the button stays clickable; repeated clicks while a request runs are ignored
    if (loading) return

    const email = emailRef.current?.value.trim() ?? ""
    const password = passwordRef.current?.value ?? ""
    const errors: FieldErrors = {}
    if (!email) errors.email = "Gib deine E-Mail-Adresse ein."
    if (!password) errors.password = "Gib dein Passwort ein."
    if (errors.email || errors.password) {
      setFieldErrors(errors)
      ;(errors.email ? emailRef : passwordRef).current?.focus()
      return
    }

    setLoading(true)
    setFormError("")
    const { error } = await authClient.signIn.email({ email, password })
    if (error) {
      setFormError(
        authErrorMessage(
          error,
          "Anmeldung fehlgeschlagen. Versuch es noch einmal."
        )
      )
      setLoading(false)
      return
    }
    router.replace("/")
    router.refresh()
  }

  const signInWithGoogle = async () => {
    setFormError("")
    setLoading(true)
    const { error } = await authClient.signIn.social({
      provider: "google",
      callbackURL: "/",
      errorCallbackURL: "/login",
    })
    if (error) {
      setFormError(
        authErrorMessage(error, "Anmeldung mit Google fehlgeschlagen.")
      )
      setLoading(false)
    }
  }

  return (
    <Card className={AUTH_CARD}>
      <CardHeader>
        <CardTitle className={AUTH_TITLE}>Willkommen zurück</CardTitle>
        <CardDescription>
          Melde dich mit Google oder deiner E-Mail-Adresse an.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} noValidate>
          <FieldGroup>
            <Field>
              <Button
                type="button"
                variant="outline"
                className={AUTH_SECONDARY}
                onClick={signInWithGoogle}
              >
                <GoogleIcon />
                Mit Google anmelden
              </Button>
            </Field>
            <FieldSeparator className="*:data-[slot=field-separator-content]:bg-transparent">
              oder
            </FieldSeparator>

            <Field data-invalid={!!fieldErrors.email || undefined}>
              <FieldLabel htmlFor="login-email">E-Mail</FieldLabel>
              <Input
                ref={emailRef}
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="name@beispiel.de"
                aria-invalid={!!fieldErrors.email || undefined}
                onChange={() => clearError("email")}
              />
              <FieldError>{fieldErrors.email}</FieldError>
            </Field>
            <Field data-invalid={!!fieldErrors.password || undefined}>
              <FieldLabel htmlFor="login-password">Passwort</FieldLabel>
              <PasswordInput
                ref={passwordRef}
                id="login-password"
                name="password"
                autoComplete="current-password"
                aria-invalid={!!fieldErrors.password || undefined}
                onChange={() => clearError("password")}
              />
              <FieldError>{fieldErrors.password}</FieldError>
            </Field>

            {formError && (
              <p
                role="alert"
                className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {formError}
              </p>
            )}

            <Field>
              <Button
                type="submit"
                size="lg"
                className={AUTH_SUBMIT}
                aria-busy={loading}
              >
                {loading && <Loader2 className="animate-spin" />}
                Anmelden
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Noch kein Konto?{" "}
                <Link href="/signup" className={AUTH_LINK}>
                  Registrieren
                </Link>
              </p>
            </Field>
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  )
}
